import { cloneElement, forwardRef, isValidElement } from 'react'
import type { ComponentPropsWithRef, FunctionComponent, ReactElement } from 'react'
import { css, cva } from '~/styled-system/css'
import type { RecipeVariantProps } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import type { WithCss } from '~/styled-system/types'

const button = cva({
  base: {
    display: 'inline-block',
    borderRadius: 'md',
    lineHeight: 'none',
  },
  variants: {
    color: {
      primary: {
        backgroundGradient: 'button.primary',
        fontWeight: 'bold',
        color: 'white',
      },
      secondary: {
        backgroundGradient: 'button.secondary',
        fontWeight: 'bold',
        color: 'white',
      },
      secondarySolid: {
        backgroundColor: 'button.secondary.solid',
        fontWeight: 'bold',
        color: 'white',
      },
      tertiary: {
        borderWidth: '[1px]',
        borderColor: 'button.tertiary.border',
        backgroundColor: 'white',
        color: 'button.tertiary.text',
      },
      normal: {
        borderWidth: '[1px]',
        borderColor: 'button.normal.border',
        backgroundColor: 'button.normal.background',
        color: 'button.normal.text',
      },
      // TODO:
      //navigate: 'bg-accent-beige text-accent-brown',
      //login: 'bg-accent-brightgreen font-bold text-neutral-000',
      //yahoo: 'border border-accent-pink bg-neutral-000 text-accent-pink',
      //paypay: 'border border-neutral-400 bg-neutral-000',
    },
    size: {
      xs: {
        paddingX: '3',
        paddingY: '2',
        fontSize: 'sm',
      },
      sm: {
        paddingX: '4',
        paddingY: '3',
        fontSize: 'base',
      },
      md: {
        paddingX: '4',
        paddingY: '3',
        fontSize: 'xl',
      },
      lg: {
        paddingX: '5',
        paddingY: '4',
        fontSize: 'xl',
      },
      // PC の検索ボックスだけ...
      pcSearch: {
        paddingX: '10',
        py: '3',
        fontSize: 'lg',
      },
    },
    disabled: {
      true: {
        backgroundColor: 'gray.400',
        // background-image でグラデーションしている場合を無効化する
        backgroundImage: 'none',
        color: 'white',
      },
      false: {
        transition: 'opacity',
        transitionDuration: '[200ms]',
        _hover: {
          opacity: '75',
        },
      },
    },
    spread: {
      true: { width: 'full' },
    },
  },
  defaultVariants: {
    color: 'normal',
    size: 'md',
  },
})

type Props = RecipeVariantProps<typeof button> &
  WithCss & {
    leftIcon?: ReactElement
    rightIcon?: ReactElement
  } & (
    | { asChild: true; children: ReactElement }
    | ({ asChild?: false } & ComponentPropsWithRef<'button'>)
  )

// Button <https://www.figma.com/design/Xizn2jb2WlVtzaAoZf0maY/Master?node-id=24149-95375&t=gSMatooJEORt7O5m-4>
export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    color,
    size,
    spread = false,
    disabled = false,
    css: cssProp,
    leftIcon,
    rightIcon,
    asChild = false,
    children,
    ...props
  },
  ref
) {
  const actAsChild = asChild && isValidElement(children)
  const content = actAsChild ? children.props.children : children

  return cloneElement(
    actAsChild ? (
      disabled ? (
        <div aria-disabled />
      ) : (
        children
      )
    ) : (
      <button ref={ref} type="button" disabled={disabled} {...props} />
    ),
    {
      className: css(
        button.raw({ color, size, disabled, spread }),
        leftIcon || rightIcon
          ? hstack.raw({ display: 'inline-flex', justifyContent: 'center', gap: '1' })
          : null,
        cssProp
      ),
    },
    leftIcon || rightIcon ? (
      <>
        {leftIcon}
        {content}
        {rightIcon}
      </>
    ) : (
      content
    )
  )
}) as FunctionComponent<Props>
